/**
 * Tipos do blob `config` de `store_checkouts` (backend) — schemaVersion 1.
 * Espelha o shape descrito no plano "Sistema de Checkouts (rascunho/publicado) por loja".
 *
 * Fonte única do visual do checkout: `checkout.service.ts` (backend) sempre resolve
 * (e provisiona, se preciso) o tema publicado da loja e o entrega em
 * `shopifyOffer.checkoutTheme.checkout.config`.
 */

export interface CheckoutHeaderConfig {
  logo?: string;
  backgroundColor: string;
  textColor: string;
  showSecurityIcon: boolean;
  /** "em breve" — sem UI ainda no frontv2. */
  showScarcityBar: boolean;
}

export interface CheckoutSummaryConfig {
  defaultOpenOnMobile: boolean;
  showInstallments: boolean;
  showCoupon: boolean;
}

export interface CheckoutFooterConfig {
  backgroundColor: string;
  textColor: string;
  showSecurityBadge: boolean;
  /**
   * IDs do catálogo global `PaymentBadge` habilitados para este checkout.
   * `'all'` = sentinela usado pelo default legado (equivalente a "mostrar tudo",
   * já que fora do modo preview não há catálogo real disponível no payload da oferta).
   */
  enabledPaymentBadgeIds: number[] | 'all';
  showWhatsapp: boolean;
  showPhone: boolean;
  showAddress: boolean;
  showCnpj: boolean;
}

export type CheckoutBorderRadius = 'square' | 'rounded' | 'full';

export interface CheckoutGeneralConfig {
  favicon?: string;
  /** Só 'layout1' é funcional hoje; layout2/3 são "em breve". */
  layout: 'layout1' | 'layout2' | 'layout3';
  borderRadius: CheckoutBorderRadius;
  buttonColor: string;
  /** Só o font default está ativo nesta fase; 2-6 são "em breve". */
  font: string;
}

export interface CheckoutIdentificationStepConfig {
  requireCpfOnlyAtPayment: boolean;
  requestBirthDate: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface CheckoutDeliveryStepConfig {}

export interface CheckoutPaymentStepConfig {
  preferredPaymentMethod?: string;
  /** "em breve" — sem UI ainda no frontv2. */
  showImmediateApprovalTag: boolean;
}

export interface CheckoutStepsConfig {
  identification: CheckoutIdentificationStepConfig;
  delivery: CheckoutDeliveryStepConfig;
  payment: CheckoutPaymentStepConfig;
}

export interface CheckoutConfig {
  schemaVersion: 1;
  header: CheckoutHeaderConfig;
  summary: CheckoutSummaryConfig;
  footer: CheckoutFooterConfig;
  general: CheckoutGeneralConfig;
  steps: CheckoutStepsConfig;
}

/** Espelha o catálogo global `PaymentBadge` (backend). */
export interface PaymentBadgeDto {
  id: number;
  key: string;
  label: string;
  iconUrl: string;
}

/** Espelha os dados de contato lidos de `Store` (não fazem parte do `config`). */
export interface StoreContactDto {
  whatsapp?: string | null;
  phone?: string | null;
  address?: string | null;
  cnpj?: string | null;
}

/** Payload composto de `GET /store-checkouts/preview/:nanoId` (rota pública). */
export interface CheckoutPreviewResponseDto {
  checkout: {
    nanoId: string;
    name: string;
    status: 'draft' | 'published';
    config: CheckoutConfig;
  };
  storeContact: StoreContactDto;
  paymentBadges: PaymentBadgeDto[];
}

/** Mensagem aceita via `postMessage` vinda do iframe de prévia do admin. */
export interface CheckoutPreviewUpdateMessage {
  type: 'checkout-preview-update';
  config: Partial<CheckoutConfig>;
}

export function isCheckoutPreviewUpdateMessage(data: unknown): data is CheckoutPreviewUpdateMessage {
  if (!data || typeof data !== 'object') return false;
  const maybe = data as Record<string, unknown>;
  return maybe.type === 'checkout-preview-update' && typeof maybe.config === 'object' && maybe.config !== null;
}
